import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

const plans = [
  {
    name: 'Free',
    tag: 'No credit card',
    monthlyPrice: 0,
    yearlyPrice: 0,
    desc: 'Explore Pulse with no commitment.',
    cta: 'Get Started Free',
    popular: false,
    gradA: '#334155', gradB: '#1e293b',
    features: [
      { text: 'Pulse AI Assistant' },
      { text: 'Web & WhatsApp Support' },
      { text: '500 messages / month' },
      { text: '1 website training / month' },
      { text: '1 document training / month' },
    ],
  },
  {
    name: 'Starter',
    tag: null,
    monthlyPrice: 999,
    yearlyPrice: 799,
    desc: 'For small teams ready to automate customer support.',
    cta: 'Get this plan',
    popular: false,
    gradA: '#479EC3', gradB: '#3B88A9',
    features: [
      { text: 'Pulse AI Assistant' },
      { text: 'Web & WhatsApp Support' },
      { text: '5,000 messages / month', bonus: '+2,000 free' },
      { text: '2 website trainings / month' },
      { text: '3 document trainings / month' },
    ],
  },
  {
    name: 'Growth',
    tag: 'Most Popular',
    monthlyPrice: 1999,
    yearlyPrice: 1599,
    desc: 'Best value for growing businesses scaling support.',
    cta: 'Get this plan',
    popular: true,
    gradA: '#479EC3', gradB: '#22D3EE',
    features: [
      { text: 'Pulse AI Assistant' },
      { text: 'Web & WhatsApp Support' },
      { text: '12,000 messages / month', bonus: '+4,000 free' },
      { text: '3 website trainings / month' },
      { text: '5 document trainings / month' },
    ],
  },
  {
    name: 'Pro',
    tag: null,
    monthlyPrice: 4999,
    yearlyPrice: 3999,
    desc: 'Power through peak demand with maximum capacity.',
    cta: 'Get this plan',
    popular: false,
    gradA: '#6366f1', gradB: '#479EC3',
    features: [
      { text: 'Pulse AI Assistant' },
      { text: 'Web & WhatsApp Support' },
      { text: '30,000 messages / month', bonus: '+10,000 free' },
      { text: '10 website trainings / month' },
      { text: '10 document trainings / month' },
    ],
  },
];

export default function Pricing() {
  const [yearly, setYearly] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  return (
    <section id="pricing" style={{ padding: '100px 24px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: '50%', transform: 'translateX(-50%)', width: 700, height: 300, background: 'rgba(71,158,195,0.05)', borderRadius: '50%', filter: 'blur(100px)', pointerEvents: 'none' }} />

      <div style={{ maxWidth: 1200, margin: '0 auto', position: 'relative', zIndex: 1 }}>
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          style={{ textAlign: 'center', marginBottom: 52 }}>
          <p style={{ fontSize: 13, color: '#479EC3', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 12 }}>Pricing</p>
          <h2 style={{ fontSize: 'clamp(28px, 4vw, 48px)', fontWeight: 800, lineHeight: 1.15, marginBottom: 14, letterSpacing: '-0.02em' }}>
            Start free. Scale when ready.
          </h2>
          <p style={{ fontSize: 17, color: '#64748b', marginBottom: 28 }}>No credit card required to get started.</p>

          {/* Toggle */}
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 12, padding: '6px 16px', background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(30,41,59,0.8)', borderRadius: 999 }}>
            <span style={{ fontSize: 14, color: yearly ? '#64748b' : '#f1f5f9', fontWeight: yearly ? 400 : 600 }}>Monthly</span>
            <button onClick={() => setYearly(v => !v)} style={{
              width: 44, height: 24, borderRadius: 999, border: 'none', cursor: 'pointer', position: 'relative',
              background: yearly ? 'linear-gradient(135deg,#479EC3,#22D3EE)' : 'rgba(30,41,59,0.8)',
              transition: 'background 0.3s',
            }}>
              <motion.div animate={{ x: yearly ? 20 : 2 }} transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                style={{ position: 'absolute', top: 2, left: 0, width: 20, height: 20, borderRadius: '50%', background: '#fff' }} />
            </button>
            <span style={{ fontSize: 14, color: yearly ? '#f1f5f9' : '#64748b', fontWeight: yearly ? 600 : 400 }}>
              Yearly <span style={{ color: '#22D3EE', fontWeight: 600 }}>(-20%)</span>
            </span>
          </div>
        </motion.div>

        <div className="pricing-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 18, alignItems: 'stretch' }}>
          {plans.map((plan, i) => (
            <motion.div key={plan.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              style={{
                position: 'relative',
                background: plan.popular
                  ? `linear-gradient(160deg,${plan.gradA}22,${plan.gradB}18)`
                  : 'rgba(15,23,42,0.65)',
                backdropFilter: 'blur(20px)',
                border: plan.popular ? `1.5px solid ${plan.gradA}66` : '1px solid rgba(30,41,59,0.6)',
                borderRadius: 18, padding: '28px 24px',
                boxShadow: plan.popular ? `0 0 48px ${plan.gradA}28` : 'none',
              }}>

              {plan.tag && (
                <div style={{
                  position: 'absolute', top: -14, left: '50%', transform: 'translateX(-50%)',
                  padding: '4px 14px', borderRadius: 999, fontSize: 11.5, fontWeight: 700,
                  background: plan.popular ? 'linear-gradient(135deg,#479EC3,#22D3EE)' : 'rgba(30,41,59,0.9)',
                  color: '#fff', whiteSpace: 'nowrap', border: plan.popular ? 'none' : '1px solid rgba(255,255,255,0.1)',
                }}>{plan.tag}</div>
              )}

              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#f1f5f9', marginBottom: 6 }}>{plan.name}</h3>
              <p style={{ fontSize: 12.5, color: '#64748b', marginBottom: 20, lineHeight: 1.55, minHeight: 40 }}>{plan.desc}</p>

              {/* Price */}
              <div style={{ marginBottom: 22, minHeight: 64 }}>
                {plan.monthlyPrice === 0 ? (
                  <span style={{ fontSize: 36, fontWeight: 800, color: '#f1f5f9' }}>Free</span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2 }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>₹</span>
                    <span style={{ fontSize: 38, fontWeight: 800, color: '#f1f5f9', lineHeight: 1 }}>
                      {(yearly ? plan.yearlyPrice : plan.monthlyPrice).toLocaleString('en-IN')}
                    </span>
                    <span style={{ fontSize: 13, color: '#64748b', marginBottom: 4 }}>/mo</span>
                  </div>
                )}
                {yearly && plan.monthlyPrice > 0 && (
                  <p style={{ fontSize: 11.5, color: '#22D3EE', marginTop: 4 }}>
                    Save ₹{((plan.monthlyPrice - plan.yearlyPrice) * 12).toLocaleString('en-IN')}/year
                  </p>
                )}
              </div>

              <motion.button whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                onClick={() => navigate(user ? '/dashboard' : '/auth')}
                style={{
                  width: '100%', padding: '11px 0', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontSize: 14, fontWeight: 700, marginBottom: 24,
                  background: plan.popular
                    ? `linear-gradient(135deg,${plan.gradA},${plan.gradB})`
                    : plan.monthlyPrice === 0
                    ? 'rgba(255,255,255,0.06)'
                    : `rgba(${plan.gradA === '#479EC3' ? '71,158,195' : '99,102,241'},0.15)`,
                  color: '#fff',
                  border: plan.popular ? 'none' : `1px solid ${plan.monthlyPrice === 0 ? 'rgba(255,255,255,0.1)' : plan.gradA + '44'}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                {plan.cta} {plan.popular && <ArrowRight size={15} />}
              </motion.button>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {plan.features.map((f, fi) => (
                  <div key={fi} style={{ display: 'flex', alignItems: 'flex-start', gap: 9 }}>
                    <Check size={14} color={plan.popular ? '#22D3EE' : '#479EC3'} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span style={{ fontSize: 13, color: '#94a3b8', lineHeight: 1.45 }}>
                      {f.text}
                      {f.bonus && (
                        <span style={{
                          marginLeft: 6, fontSize: 10.5, fontWeight: 700, color: '#22D3EE',
                          background: 'rgba(34,211,238,0.12)', border: '1px solid rgba(34,211,238,0.25)',
                          borderRadius: 4, padding: '1px 5px', verticalAlign: 'middle', whiteSpace: 'nowrap',
                        }}>
                          <Zap size={9} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 2 }} />
                          {f.bonus}
                        </span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        <p style={{ textAlign: 'center', marginTop: 28, fontSize: 13, color: '#475569' }}>
          Bonus messages are a limited-time offer · Cancel anytime
        </p>
      </div>

      <style>{`
        @media (max-width: 1024px) { .pricing-grid { grid-template-columns: repeat(2, 1fr) !important; } }
        @media (max-width: 560px)  { .pricing-grid { grid-template-columns: 1fr !important; } }
      `}</style>
    </section>
  );
}
